/**
 * `GET /dashboard/farms-twin` — хозяйства линзы с заполненностью двойника
 * (AK-903, экран «Холдинг», макет v2 экран 4).
 *
 * Множество хозяйств, сортировку, поиск, страницу и ИТОГИ считает сервер:
 * `totals` — по всей выборке (линза ∩ граница чтения ∩ поиск), не по
 * загруженным строкам (правило «итог-карточка считает выборку, не страницу»).
 * Клиент ничего не складывает и не сортирует сам — на второй странице это
 * было бы враньём.
 *
 * Заголовки шагов («что не заполнено») — из `steps` ответа: словарь шагов
 * живёт на сервере (`farm_twin_steps.STEP_TITLES`), переименовали там —
 * переименовалось здесь.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { TwinStepState } from '@/components/farm/FarmTwinStrip';
import { api } from '@/lib/api';

export type FarmsTwinSort = 'incomplete_first' | 'cluster' | 'name';

export interface FarmTwinStepOut {
  key: string;
  done: boolean;
  state: TwinStepState;
}

export interface FarmTwinRow {
  id: string;
  name: string;
  cluster_id: string | null;
  cluster_name: string | null;
  /** Учётная пашня, га (правило founder 25.08) — как в карточке хозяйства. */
  arable_ha: number;
  fields_active: number;
  done_count: number;
  total_steps: number;
  /** Ключи незакрытых шагов в порядке полосы. */
  missing: string[];
  steps: FarmTwinStepOut[];
}

export interface FarmsTwinTotals {
  farms: number;
  clusters: number;
  arable_ha: number;
  fields_active: number;
  farms_complete: number;
  total_steps: number;
}

export interface FarmsTwinStepTitle {
  key: string;
  title: string;
}

export interface FarmsTwinOut {
  farms: FarmTwinRow[];
  totals: FarmsTwinTotals;
  steps: FarmsTwinStepTitle[];
  total: number;
  limit: number;
  offset: number;
  sort: FarmsTwinSort;
  q: string | null;
  computed_at: string;
}

export interface FarmsTwinParams {
  sort: FarmsTwinSort;
  q: string;
  limit: number;
}

export const FARMS_TWIN_PAGE = 60;

export const DEFAULT_FARMS_TWIN_PARAMS: FarmsTwinParams = {
  sort: 'incomplete_first',
  q: '',
  limit: FARMS_TWIN_PAGE,
};

export const farmsTwinKeys = {
  all: ['dashboard', 'farms-twin'] as const,
  list: (p: FarmsTwinParams) => ['dashboard', 'farms-twin', p.sort, p.q, p.limit] as const,
};

export function farmsTwinPath(p: FarmsTwinParams): string {
  const search = new URLSearchParams({ sort: p.sort, limit: String(p.limit) });
  if (p.q.trim()) search.set('q', p.q.trim());
  return `/api/v1/dashboard/farms-twin?${search.toString()}`;
}

export function useFarmsTwin(p: FarmsTwinParams, enabled = true): UseQueryResult<FarmsTwinOut> {
  return useQuery({
    queryKey: farmsTwinKeys.list(p),
    queryFn: () => api.get<FarmsTwinOut>(farmsTwinPath(p)),
    enabled,
    staleTime: 30_000,
    // Поиск: пока печатают, предыдущая страница остаётся на экране — таблица
    // не мигает пустотой между буквами.
    placeholderData: (prev) => prev,
  });
}

/**
 * «Что не заполнено» словами: заголовки незакрытых шагов через запятую, в
 * порядке полосы. Шаг без источника (`not_available_yet`, люди до ADR-082)
 * помечается — он не заполнен не по вине хозяйства.
 */
export function missingTitles(row: FarmTwinRow, steps: FarmsTwinStepTitle[]): string {
  const titles = new Map(steps.map((s) => [s.key, s.title]));
  const states = new Map(row.steps.map((s) => [s.key, s.state]));
  const parts = row.missing.map((key) => {
    const title = titles.get(key) ?? key;
    return states.get(key) === 'not_available_yet' ? `${title} (появится позже)` : title;
  });
  return parts.length ? parts.join(', ') : '—';
}

export function formatHa(ha: number): string {
  return `${Math.round(ha).toLocaleString('ru-RU')} га`;
}
